<template>
  <div
    class="gallery"
    v-observe-visibility="visibilityObserve"
    :class="classObj"
  >
    <div v-if="!single" class="flex flex-no-wrap justify-between mb-2 sm:mb-4">
      <div class="flex-auto flex-grow-0 pr-4">
        <button @click.prevent="previous">
          <template v-if="screens.sm">
            previous {{ previousSlideType }}
          </template>
          <template v-else>
            &lt;
          </template>
        </button>
      </div>
      <div class="flex-auto text-center">
        <template v-if="longLoad">
          loading
        </template>
        <template v-else>
          [{{ currentIndex }}/{{ total }}]
        </template>
      </div>
      <div class="flex-auto flex-grow-0">
        <button @click.prevent="next">
          <template v-if="screens.sm">
            next {{ nextSlideType }}
          </template>
          <template v-else>
            &gt;
          </template>
        </button>
      </div>
    </div>
    <div class="gallery__media relative" :style="mediaStyle">
      <transition
        appear
        :css="false"
        @before-enter="beforeEnter"
        @enter="enter"
        @after-enter="afterEnter"
        @enter-cancelled="enterCancelled"
      >
        <media
          v-if="activeSlide"
          v-bind="activeSlide"
          ref="media"
          :key="`slide-${activeIndex}`"
          class="w-full h-auto max-h-screen object-contain object-center"
          params="?fit=max&w=2400&h=1800"
          @click.native="next"
        />
      </transition>
      <rich-text
        v-if="caption"
        :text="caption"
        class="gallery__caption text-xs p-1 lg:text-base"
      />
      <canvas
        :width="width"
        :height="height"
        class="w-full h-full top-0 left-0 absolute"
        ref="canvas"
        crossorigin="anonymous"
        aria-hidden
      />
    </div>
  </div>
</template>

<script>
import Hammer from 'hammerjs'
import imagesLoaded from 'imagesloaded'
import wait from 'waait'
import { mapGetters } from 'vuex'
import Media from '@/app/components/Media.vue'
import RichText from '@/app/components/RichText.vue'
import { SCREENS } from '@/store/types'

export default {
  name: 'Gallery',

  components: {
    Media,
    RichText
  },

  props: {
    slides: {
      default: () => [],
      type: Array
    }
  },

  data() {
    return {
      activeIndex: 0,
      width: 0,
      height: 0,
      hammer: null,
      canvas: null,
      loading: false,
      longLoad: false,
      longDelay: 1500,
      loadTimer: null,
      initialized: false,
      visible: false
    }
  },

  computed: {
    ...mapGetters({
      screens: `screen/${SCREENS}`
    }),

    activeSlide() {
      const slides = this.slides
      const index = this.activeIndex
      return slides[index]
    },

    single() {
      return this.slides.length === 1
    },

    total() {
      return this.slides.length
    },

    currentIndex() {
      return this.activeIndex + 1
    },

    nextIndex() {
      const slides = this.slides
      const nextIndex = this.activeIndex + 1
      const nextSlide = slides[nextIndex]
      return nextSlide ? nextIndex : 0
    },

    previousIndex() {
      const slides = this.slides
      const previousIndex = this.activeIndex - 1
      const previousSlide = slides[previousIndex]
      return previousSlide ? previousIndex : slides.length - 1
    },

    nextSlide() {
      return this.slides[this.nextIndex]
    },

    nextSlideType() {
      return this.nextSlide.image ? 'image' : 'video'
    },

    previousSlide() {
      return this.slides[this.previousIndex]
    },

    previousSlideType() {
      return this.previousSlide.image ? 'image' : 'video'
    },

    images() {
      return this.slides.filter(s => s.image).map(s => s.image)
    },

    posters() {
      return this.slides
        .filter(s => s.video && s.video.poster)
        .map(s => s.video.poster)
    },

    caption() {
      return this.activeSlide.caption
    },

    classObj() {
      return {
        'is-loading': this.loading
      }
    },

    mediaStyle() {
      return {
        minHeight: `${this.height}px`
      }
    },

    visibilityObserve() {
      const init = this.initialized
      if (!init) {
        return false
      }
      return {
        callback: this.visibilityChange,
        threshold: 0.3,
        once: true
      }
    }
  },

  mounted() {
    const media = this.$el.querySelector('.gallery__media')
    this.hammer = new Hammer(media, { touchAction: 'pan-y' })
    this.hammer.get('swipe').set({ direction: Hammer.DIRECTION_HORIZONTAL })
    this.hammer.on('swipe', this.swipe)
    this.canvas = this.$refs.canvas.getContext('2d')
    this.$bus.$on('resize', this.setSize)
  },

  beforeDestroy() {
    this.stopLoading()
    this.hammer.destroy()
    this.hammer = null
    this.$bus.$off('resize', this.setSize)
  },

  methods: {
    next() {
      if (!this.loading) {
        this.activeIndex = this.nextIndex
      }
    },

    previous() {
      if (!this.loading) {
        this.activeIndex = this.previousIndex
      }
    },

    swipe({ direction }) {
      switch (direction) {
        case Hammer.DIRECTION_LEFT:
          this.next()
          break
        case Hammer.DIRECTION_RIGHT:
          this.previous()
          break
      }
    },

    preload() {
      const images = this.images.map(i => i.url)
      const posters = this.posters.map(p => p.url)
      this.$imagePreload([...images, ...posters])
    },

    disableSmoothRendering(ctx) {
      ctx.webkitImageSmoothingEnabled = false
      ctx.mozImageSmoothingEnabled = false
      ctx.msImageSmoothingEnabled = false
      ctx.imageSmoothingEnabled = false
      return ctx
    },

    pixelate(img, amount = 0.025) {
      const width = this.width
      const height = this.height
      const pWidth = Math.floor(width * (amount <= 0 ? 0.01 : amount))
      const pHeight = Math.floor(height * (amount <= 0 ? 0.01 : amount))
      const canvas = this.$refs.canvas
      // disable smoothness
      this.canvas = this.disableSmoothRendering(this.canvas)
      // render smaller image
      this.canvas.drawImage(img, 0, 0, pWidth, pHeight)
      // stretch the smaller image
      this.canvas.drawImage(canvas, 0, 0, pWidth, pHeight, 0, 0, width, height)
    },

    async animatePixels(el) {
      const canvas = this.$refs.canvas
      canvas.style.display = 'block'
      await wait()
      this.pixelate(el)
      await wait(200)
      this.pixelate(el, 0.01)
      await wait(100)
      this.pixelate(el, 0.05)
      await wait(100)
      this.pixelate(el, 0.1)
      await wait(100)
      this.pixelate(el, 0.5)
      canvas.style.display = 'none'
    },

    async loadImage(el) {
      await new Promise(resolve => {
        imagesLoaded(el, resolve)
      })
      await wait()
      this.setSize(el)
      await this.animatePixels(el)
    },

    async loadVideo(el) {
      const poster = this.activeSlide.poster
      let image
      if (poster) {
        image = document.createElement('img')
        image.crossOrigin = 'anonymous'
        image.src = poster.url
        await new Promise(resolve => {
          imagesLoaded(el, resolve)
        })
        await wait(50)
        this.setSize(el)
        await this.animatePixels(image)
      } else {
        await wait()
        this.setSize(el)
      }
    },

    setSize(el = this.$refs.media.$el) {
      const width = el.clientWidth
      const height = el.clientHeight
      if (el && height) {
        this.width = width
        this.height = height
      }
    },

    visibilityChange(isVisible) {
      if (isVisible) {
        this.preload()
      }
    },

    startLoading() {
      this.loading = true
      this.loadTimer = setTimeout(() => {
        if (this.loading) {
          this.longLoad = true
        }
      }, this.longDelay)
    },

    stopLoading() {
      this.loading = false
      if (this.loadTimer) {
        clearTimeout(this.loadTimer)
        this.loadTimer = null
      }
      this.longLoad = false
    },

    beforeEnter(el) {
      el.style.opacity = 0
    },

    async enter(el, done) {
      const isImage = el.nodeName.toLowerCase() === 'img'
      this.startLoading()
      if (isImage) {
        await this.loadImage(el)
      } else {
        await this.loadVideo(el)
      }
      done()
    },

    afterEnter(el) {
      this.stopLoading()
      el.style.opacity = 1
      this.initialized = true
      this.setSize()
    },

    enterCancelled() {
      this.stopLoading()
      this.initialized = true
    }
  }
}
</script>

<style scoped>
.gallery {
  user-select: none;
}

.gallery.is-loading {
  cursor: progress;
}

.gallery__media {
  touch-action: pan-y;
}

.gallery__caption {
  position: absolute;
  text-align: right;
  top: 100%;
  width: 100%;
}

canvas {
  image-rendering: optimizeSpeed;
  image-rendering: -moz-crisp-edges;
  image-rendering: -o-crisp-edges;
  image-rendering: -webkit-crisp-edges;
  image-rendering: crisp-edges;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: pixelated;
  -ms-interpolation-mode: nearest-neighbor;
}
</style>
